import React, { useMemo } from 'react'
import { useGameStore } from '../../state/gameStore'
import clsx from 'clsx'
import { Target, Split, CheckCircle2, Undo2, GitBranch, Activity } from 'lucide-react'
import { computeAABBFromInequalities } from '../../../graphics/geometry'
import { appBus } from '../../../app/app-events'

const BnBInspector: React.FC = () => {
  const { currentNodeId, nodes, incumbentNodeId, commandLocked, phase } = useGameStore((state) => state.ui.bnbState)
  const selectedNode = currentNodeId ? nodes.get(currentNodeId) : null

  const stats = useMemo(() => {
    let ub = Number.NEGATIVE_INFINITY
    let openCount = 0
    nodes.forEach((n) => {
      if (n.status === 'open' && n.lp && Number.isFinite(n.lp.optimalValue)) {
        openCount += 1
        ub = Math.max(ub, n.lp.optimalValue)
      }
    })

    let lb = Number.NEGATIVE_INFINITY
    if (incumbentNodeId) {
      const inc = nodes.get(incumbentNodeId)
      if (inc && inc.lp) lb = inc.lp.optimalValue
    }

    if (openCount === 0 && Number.isFinite(lb)) {
      ub = lb
    }

    return { lb, ub, openCount }
  }, [nodes, incumbentNodeId])

  const rootNode = useMemo(() => Array.from(nodes.values()).find((n) => !n.parentId), [nodes])
  const rootUB = rootNode?.lp?.optimalValue ?? Number.POSITIVE_INFINITY

  const isBinaryProblem = useMemo(() => {
    if (!rootNode) return false
    const aabb = computeAABBFromInequalities(rootNode.problem.inequalities)
    return (
      aabb.xMin >= -0.001 &&
      aabb.xMax <= 1.001 &&
      aabb.yMin >= -0.001 &&
      aabb.yMax <= 1.001 &&
      aabb.zMin >= -0.001 &&
      aabb.zMax <= 1.001
    )
  }, [rootNode])

  const formatVal = (v: number) => (Number.isFinite(v) ? v.toFixed(2) : '—')

  let gapClosedPercent = 0
  if (Number.isFinite(stats.lb) && Number.isFinite(rootUB)) {
    const totalGap = rootUB - stats.lb
    const currentGap = stats.ub - stats.lb

    if (Math.abs(totalGap) < 1e-9) {
      gapClosedPercent = 100
    } else {
      const remainingRatio = Math.max(0, currentGap / totalGap)
      gapClosedPercent = (1 - remainingRatio) * 100
    }
  }

  gapClosedPercent = Math.max(0, Math.min(100, gapClosedPercent))

  const isOptimal = stats.openCount === 0 || (Number.isFinite(stats.ub) && Number.isFinite(stats.lb) && Math.abs(stats.ub - stats.lb) < 1e-6)

  return (
    <div className="flex flex-col h-full min-h-0 bg-slate-900/50 rounded-xl border border-white/10 p-3 shadow-sm backdrop-blur-sm relative overflow-hidden">
      <div className="mb-4 pb-4 border-b border-white/5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-slate-300">
            <Activity size={14} className="text-indigo-400" />
            <span className="text-xs font-bold tracking-widest">Global Status</span>
          </div>
          <div
            className={clsx(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide border',
              isOptimal ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20',
            )}
          >
            {isOptimal ? <CheckCircle2 size={10} /> : <Activity size={10} />}
            {isOptimal ? 'Optimal' : phase === 'branching' || phase === 'backtracking' ? 'Busy' : 'Active'}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="bg-black/20 rounded-lg p-3 border border-white/5 flex flex-col items-center justify-center">
            <div className="text-xs text-slate-300 font-bold mb-1 tracking-wider">Lower Bound</div>
            <div className="font-mono text-xl font-bold text-emerald-400">{formatVal(stats.lb)}</div>
          </div>
          <div className="bg-black/20 rounded-lg p-3 border border-white/5 flex flex-col items-center justify-center">
            <div className="text-xs text-slate-300 font-bold mb-1 tracking-wider">Upper Bound</div>
            <div className="font-mono text-xl font-bold text-sky-400">{formatVal(stats.ub)}</div>
          </div>
        </div>

        <div className="space-y-1.5">
          <div className="flex justify-between text-xs font-medium">
            <span className="text-slate-400">Gap Closed</span>
            <span className="text-white font-mono font-bold">{gapClosedPercent.toFixed(2)}%</span>
          </div>
          <div className="h-1.5 w-full bg-slate-800 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-emerald-500 to-sky-500 transition-all duration-500" style={{ width: `${gapClosedPercent}%` }} />
          </div>
        </div>
      </div>

      {selectedNode ? (
        <div className="flex flex-col h-full min-h-0 z-10">
          <div className="bg-black/20 rounded-lg p-3 border border-white/5 mb-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-bold text-white">Node #{selectedNode.id}</span>
                <span
                  className={clsx(
                    'text-[10px] font-bold px-2 py-0.5 rounded border',
                    selectedNode.status === 'open' && 'bg-sky-500/10 text-sky-400 border-sky-500/20',
                    selectedNode.status === 'pruned' && 'bg-red-500/10 text-red-400 border-red-500/20',
                    selectedNode.status === 'fathomed' && 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
                    !['open', 'pruned', 'fathomed'].includes(selectedNode.status) && 'bg-slate-700/30 text-slate-400 border-white/5',
                  )}
                >
                  {selectedNode.status}
                </span>
              </div>
              <div className="text-right">
                <div className="text-xs text-slate-400 font-bold">LP Value</div>
                <div className="font-mono text-base font-bold text-white">
                  {selectedNode.lp?.optimalValue !== undefined ? selectedNode.lp.optimalValue.toFixed(2) : 'Inf'}
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-2 pt-2 border-t border-white/5">
              <button
                onClick={() => {
                  if (!commandLocked && selectedNode.children.length > 0) {
                    appBus.emit('bnb-backtrack-requested', { nodeId: selectedNode.id })
                  }
                }}
                disabled={commandLocked || selectedNode.children.length === 0}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded text-xs font-bold transition-all',
                  !commandLocked && selectedNode.children.length > 0
                    ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20'
                    : 'bg-slate-800/50 text-slate-600 border border-white/5 cursor-not-allowed',
                )}
              >
                <Undo2 size={12} />
                Reset Subtree
              </button>
            </div>

            {selectedNode.lp?.solution && (
              <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0 -mr-1 pr-1">
                <div className="flex items-center gap-2 mb-3 mt-4">
                  <GitBranch size={14} className="text-slate-400" />
                  <div className="text-xs font-bold text-slate-300">Branching Candidates</div>
                </div>

                <div className="space-y-2">
                  {selectedNode.lp.solution.map((val, idx) => {
                    if (idx > 2) return null

                    const isInteger = Math.abs(val - Math.round(val)) < 1e-6
                    const isNodeOpen = selectedNode.status === 'open'
                    const canBranch = isNodeOpen && (!isInteger || isBinaryProblem)

                    const vars = ['x', 'y', 'z']
                    const vName = vars[idx]

                    return (
                      <div
                        key={idx}
                        className="bg-black/20 border border-white/5 rounded-lg p-3 group hover:bg-slate-800/50 transition-colors flex items-center justify-between gap-2"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="w-6 h-6 rounded bg-cyan-500/10 text-cyan-400 flex items-center justify-center font-mono text-xs font-bold border border-cyan-500/20 flex-shrink-0">
                            {vName}
                          </div>
                          <span className="text-sm font-mono text-white font-bold whitespace-nowrap">= {val.toFixed(2)}</span>
                        </div>

                        <button
                          onClick={() => {
                            if (!commandLocked && canBranch) {
                              appBus.emit('bnb-branch-requested', { varIndex: idx, mode: 'both', nodeId: selectedNode.id })
                            }
                          }}
                          disabled={commandLocked || !canBranch}
                          className={clsx(
                            'flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] font-bold rounded shadow-sm transition-colors flex-shrink-0',
                            !commandLocked && canBranch
                              ? 'bg-cyan-500 hover:bg-cyan-400 text-black'
                              : 'bg-slate-700 text-slate-500 cursor-not-allowed opacity-50',
                          )}
                        >
                          <Split size={12} />
                          Branch
                        </button>
                      </div>
                    )
                  })}

                  {!selectedNode.lp.infeasible && (!selectedNode.lp.solution || selectedNode.lp.hasFractionalSolution === false) && (
                    <div className="flex flex-col items-center justify-center py-6 text-slate-500 opacity-60">
                      <CheckCircle2 size={24} className="mb-2 text-emerald-500/50" />
                      <span className="text-[10px] font-medium">Integer Feasible Solution</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-600 text-center opacity-50 z-10">
          <Target size={32} className="mb-2 opacity-20" />
          <span className="text-[10px] font-medium">Select a node to inspect</span>
        </div>
      )}

      <div className="absolute top-0 right-0 p-8 opacity-[0.02] pointer-events-none">
        <Target size={120} />
      </div>
    </div>
  )
}

export default BnBInspector
